import { useFormikContext } from "formik";
import Typography from "@mui/material/Typography";
import { CreateCampaignData } from "../../../types/campaign";
import TextField from "@mui/material/TextField";
import { Autocomplete } from "../../Autocomplete";
import { Insights } from "../../../types/insights";
import Stack from "@mui/material/Stack";
import { DateTimePicker } from "../../DateTimePicker";
import { useCallback, useMemo, useState } from "react";
import { EMAILS, PHONES } from "../../../utils/constants";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import { map } from "lodash";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import { Add, DeleteOutlineRounded } from "@mui/icons-material";
import { useMutation, useQuery } from "react-query";
import {
  checkWhatsAppInstanceConnectionService,
  listWhatsAppInstanceService,
} from "../../../services/whatsApp";
import Skeleton from "@mui/material/Skeleton";
import { counterCampaignInsight } from "../../../services/campaigns";
import { useStyles } from "./styles";
import { CountUp } from "../../CountUp";
import { addDays, subMonths } from "date-fns";
import {
  createAdvertising,
  createAdvertisingProduct,
  listArts,
  putProduct,
} from "../../../services/arts";
import Divider from "@mui/material/Divider";
import { Table } from "../../Table";
import { useDialogConfirmation } from "../../../hooks/useDialogConfirmation";
import { useToast } from "../../../hooks/useToast";
import { queryClient } from "../../../services/queryClient";
import Button from "@mui/material/Button";
import { DialogAddProducts } from "../DialogAddProducts";
import { DialogNewAdvertising } from "../DialogNewAdvertising";
import { Product } from "../../../types/arts";

type InfosProps = {
  insight: Insights;
  parentCampaignEndDate?: Date;
};

export function Infos({ insight }: InfosProps) {
  const styles = useStyles();
  const toast = useToast();
  const dialog = useDialogConfirmation();

  const [dialogAddProductsOpen, setDialogAddProductsOpen] =
    useState<boolean>(false);
  const [dialogNewAdvertisingOpen, setDialogNewAdvertisingOpen] =
    useState<boolean>(false);
  const [recipientCopy, setRecipientCopy] = useState<string>("");

  const formik = useFormikContext<CreateCampaignData>();

  const rangeDate = useMemo(() => {
    return {
      endDate: addDays(new Date(), 1),
      startDate: subMonths(new Date(), 12),
    };
  }, []);

  const arts = useQuery("arts", () => listArts(rangeDate));

  const productsArt = useQuery(
    ["products", formik.values?.digitalTabloidId],
    async () => {
      const response = await listArts({
        ...rangeDate,
        tabloidId: formik.values?.digitalTabloidId,
      });

      return response[0]?.produtos;
    },
    {
      enabled:
        Boolean(formik.values) && Boolean(formik.values?.digitalTabloidId),
    }
  );

  const whatsAppInstances = useQuery(
    "whatsAppInstances",
    listWhatsAppInstanceService,
    {
      onSuccess: (response) => {
        formik.setFieldValue("whatsAppInstance", response[0]);
      },
      enabled: formik.values.channel === "whatsapp",
    }
  );

  useQuery(
    ["statusWhatsAppInstance", formik.values.whatsAppInstance?.id],
    () =>
      checkWhatsAppInstanceConnectionService(
        formik.values.whatsAppInstance?.id
      ),
    {
      onSuccess: (response) => {
        formik.setFieldValue("whatsAppInstanceStatus", response);
      },
      enabled: Boolean(formik.values.whatsAppInstance?.id),
    }
  );

  const countInsight = useQuery(
    ["countInsight", insight.idListProspectei, formik.values?.channel],
    () =>
      counterCampaignInsight(
        insight.idListProspectei,
        formik.values?.selectedRecipients,
        formik.values?.channel
      ),
    {
      enabled:
        Boolean(insight.idListProspectei) &&
        Boolean(formik.values?.channel) &&
        formik.values?.channel !== "whatsapp",
      onSuccess: (response) => {
        formik.setFieldValue("coastCampaign", response.coast);
      },
    }
  );

  const removeTabloidProduct = useMutation(
    async (data: any) => {
      const response = await putProduct(data);
      return response;
    },
    {
      onSuccess: () => {
        toast.show("Produto excluído com sucesso!", { severity: "success" });
        queryClient.invalidateQueries([
          "products",
          formik.values?.digitalTabloidId,
        ]);
        dialog.close();
      },
      onError: () => {
        toast.show("Falha ao excluir produto", { severity: "error" });
      },
    }
  );

  const newAdvertising = useMutation(
    async (advertising: string) => {
      const response = await createAdvertising(advertising);

      return response;
    },
    {
      onSuccess: () => {
        toast.show("Propaganda criada com sucesso!", { severity: "success" });
        queryClient.invalidateQueries("arts");
        handleDialogNewAdvertisingOpen();
      },
      onError: () => {
        toast.show("Falha ao criar propaganda", { severity: "error" });
      },
    }
  );

  const newAdvertisingProduct = useMutation(
    async (data: Product) => {
      const response = await createAdvertisingProduct({
        ...data,
        id_tabloide_digital: formik.values?.digitalTabloidId,
      });

      return response;
    },
    {
      onSuccess: () => {
        toast.show("Produto adicionado com sucesso!", { severity: "success" });
        queryClient.invalidateQueries([
          "products",
          formik.values?.digitalTabloidId,
        ]);
        handleDialogAddProductsOpen();
      },
      onError: () => {
        toast.show("Falha ao adicionar produto", { severity: "error" });
      },
    }
  );

  const optionsRecipients = useMemo(() => {
    if (formik.values.channel === "email") {
      if (formik.values.selectedRecipients.length === 0) {
        formik.setFieldValue("selectedRecipients", EMAILS);
      }

      return EMAILS;
    } else {
      if (formik.values.selectedRecipients.length === 0) {
        formik.setFieldValue("selectedRecipients", PHONES);
      }

      return PHONES;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik]);

  const handleAddRecipientInCopy = useCallback(() => {
    const tempRecipientInCopy = formik.values.recipientsCopy;

    tempRecipientInCopy.unshift(recipientCopy);
    setRecipientCopy("");

    formik.setFieldValue("recipientsCopy", tempRecipientInCopy);
  }, [formik, recipientCopy]);

  const handleDialogAddProductsOpen = useCallback(() => {
    setDialogAddProductsOpen((prev) => !prev);
  }, []);

  const handleDialogNewAdvertisingOpen = useCallback(() => {
    setDialogNewAdvertisingOpen((prev) => !prev);
  }, []);

  const handleDeleteRecipientInCopy = useCallback(
    (id: number) => {
      const tempRecipientInCopy = formik.values.recipientsCopy;

      tempRecipientInCopy.splice(id, 1);

      formik.setFieldValue("recipientsCopy", tempRecipientInCopy);
    },
    [formik]
  );

  const handleDialogDelete = useCallback(
    (row) => {
      dialog.show("Aviso", "Deseja apagar o produto dessa propaganda?", {
        disabled: removeTabloidProduct.isLoading,
        onConfirmate: async () => {
          const productsTemp = productsArt.data;
          const indexProduct = productsTemp?.findIndex(
            (product) => product.id_tabloide_produto === row.id_tabloide_produto
          );
          productsTemp?.splice(indexProduct, 1);
          const objectRemoveData = {
            produto: { ...row, produto_ativo: 0 },
            toUpdateSequence: {
              products: productsTemp,
              categorias: [],
            },
          };
          await removeTabloidProduct.mutateAsync(objectRemoveData);
        },
      });
    },
    [dialog, removeTabloidProduct, productsArt.data]
  );

  const handleSaveProduct = useCallback(
    async (data: Product) => {
      await newAdvertisingProduct.mutateAsync(data);
    },
    [newAdvertisingProduct]
  );

  const RenderTagsRecipientInCopy = () => {
    return (
      <Stack marginY={1} direction="row" spacing={1}>
        {map(formik.values.recipientsCopy?.slice(0, 3), (copy, id) => (
          <Chip
            key={copy}
            label={copy}
            size="small"
            onDelete={() => handleDeleteRecipientInCopy(id)}
          />
        ))}
        {formik.values.recipientsCopy?.slice(3).length > 0 ? (
          <Tooltip
            placement="top"
            title={
              <Typography variant="caption" style={{ whiteSpace: "pre-line" }}>
                {formik.values.recipientsCopy?.slice(3).join("\n")}
              </Typography>
            }
          >
            <Chip
              label={`+${formik.values.recipientsCopy?.slice(3).length}`}
              size="small"
            />
          </Tooltip>
        ) : null}
      </Stack>
    );
  };

  return (
    <Stack spacing={4}>
      {formik.values?.channel !== "whatsapp" ? (
        <Stack
          className={styles.alertPeopleInsight}
          direction="row"
          justifyContent="center"
        >
          {countInsight.isLoading ? (
            <Typography variant="button">Buscando destinatários...</Typography>
          ) : (
            <CountUp
              maxWidth="100%"
              variant="button"
              prefix="Essa audiência possui "
              suffix={
                countInsight.data.total > 1
                  ? " destinatários."
                  : " destinatário."
              }
              start={countInsight.data.total}
              end={
                countInsight.data.total + formik.values.recipientsCopy?.length
              }
            />
          )}
        </Stack>
      ) : (
        <></>
      )}

      <Typography color="primary">Informações</Typography>

      {formik.values.channel === "whatsapp" ? (
        !whatsAppInstances.isLoading ? (
          <Autocomplete
            size="small"
            label="Instâncias"
            loading={whatsAppInstances.isLoading}
            options={whatsAppInstances.data}
            getOptionLabel={(option) => option.name}
            value={formik.values.whatsAppInstance}
            onChange={(_, value) => {
              formik.setFieldValue("whatsAppInstance", value);
            }}
            error={
              !!formik.errors.whatsAppInstance ||
              !!formik.errors.whatsAppInstanceStatus
            }
            helperText={
              (!!formik.errors.whatsAppInstance &&
                (formik.errors.whatsAppInstance as string)) ||
              (!!formik.errors.whatsAppInstanceStatus &&
                (formik.errors.whatsAppInstanceStatus as string))
            }
          />
        ) : (
          <Skeleton height={60} variant="text" />
        )
      ) : (
        <></>
      )}
      <Autocomplete
        size="small"
        label="Audiência"
        disabled
        getOptionLabel={(option) => option.nameList}
        value={insight}
      />
      <Stack direction="row" spacing={2}>
        <TextField
          label="Nome"
          autoFocus
          onChange={({ target: { value } }) =>
            formik.setFieldValue("name", value)
          }
          fullWidth
          size="small"
          value={formik.values.name}
          error={!!formik.errors.name}
          helperText={!!formik.errors.name && formik.errors.name}
        />
        <TextField
          label="Cupom"
          autoFocus
          onChange={({ target: { value } }) =>
            formik.setFieldValue("coupon", value)
          }
          fullWidth
          size="small"
          value={formik.values.coupon}
        />
      </Stack>
      <Stack direction="row" spacing={2}>
        <DateTimePicker
          fullWidth
          size="small"
          label="Data de início"
          value={formik.values.startDate}
          onChange={(date) => formik.setFieldValue("startDate", date)}
          error={!!formik.errors.startDate}
          helperText={
            !!formik.errors.startDate && (formik.errors.startDate as string)
          }
        />
        <DateTimePicker
          disablePast
          fullWidth
          size="small"
          label="Data de término"
          value={formik.values.endDate}
          onChange={(date) => formik.setFieldValue("endDate", date)}
          error={!!formik.errors.endDate}
          helperText={
            !!formik.errors.endDate && (formik.errors.endDate as string)
          }
        />
        <DateTimePicker
          disablePast
          fullWidth
          size="small"
          label="Data de envio"
          value={formik.values.sendDate}
          onChange={(date) => formik.setFieldValue("sendDate", date)}
          error={!!formik.errors.sendDate}
          helperText={
            !!formik.errors.sendDate && (formik.errors.sendDate as string)
          }
        />
      </Stack>

      <Stack direction="row" spacing={2}>
        <Autocomplete
          fullWidth
          multiple
          size="small"
          label="Destinatários"
          options={optionsRecipients}
          getOptionLabel={(option) => option.label}
          onChange={(_, value) =>
            formik.setFieldValue("selectedRecipients", value)
          }
          value={formik.values.selectedRecipients || optionsRecipients}
          error={!!formik.errors.selectedRecipients}
          helperText={
            !!formik.errors.selectedRecipients &&
            (formik.errors.selectedRecipients as string)
          }
        />
        <div style={{ width: "100%" }}>
          <TextField
            label="Destinatários em cópia"
            fullWidth
            size="small"
            value={recipientCopy}
            onChange={({ target: { value } }) => setRecipientCopy(value)}
            onKeyDown={({ key }) =>
              key === "Enter" ? handleAddRecipientInCopy() : () => {}
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleAddRecipientInCopy}>
                    <Add />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {RenderTagsRecipientInCopy()}
        </div>
      </Stack>

      <Divider />
      <Typography color="primary">Mensuração</Typography>
      <Stack spacing={1} alignItems="flex-start">
        <Autocomplete
          clearOnBlur
          fullWidth
          size="small"
          label="Propagandas"
          options={arts.data as []}
          getOptionLabel={(option) => option.nome_tabloide_digital}
          onChange={(_, value) => {
            formik.setFieldValue(
              "digitalTabloidId",
              value?.id_tabloide_digital
            );
          }}
          loading={arts.isLoading}
        />
        <Button
          onClick={handleDialogNewAdvertisingOpen}
          size="small"
          variant="contained"
          color="inherit"
          disableElevation
        >
          Criar nova propaganda
        </Button>
      </Stack>

      {formik.values.digitalTabloidId && (
        <>
          <Stack direction="row" justifyContent="end">
            <Button
              onClick={handleDialogAddProductsOpen}
              variant="contained"
              size="small"
              startIcon={<Add />}
            >
              Novo produto
            </Button>
          </Stack>
          <Table
            isLoading={productsArt.isFetching}
            style={{ width: "100%" }}
            toolbar={false}
            data={productsArt.data as []}
            columns={[
              { title: "EAN", field: "ean", emptyValue: "Não encontrado" },
              {
                title: "Produto",
                field: "nome_produto",
              },
              {
                title: "Preço dois",
                field: "preco_dois",
                render: (row) => (
                  <CountUp
                    prefix="R$ "
                    decimal=","
                    decimals={2}
                    start={0}
                    end={row?.preco_dois}
                  />
                ),
              },
              {
                title: "Ações",
                render: (row) => (
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Detalhes" placement="top">
                      <IconButton
                        onClick={() => {
                          handleDialogDelete(row);
                        }}
                      >
                        <DeleteOutlineRounded />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ),
              },
            ]}
            options={{
              actionsColumnIndex: -1,
              pageSize: 3,
              selectionProps: () => ({ color: "primary" }),
            }}
          />
        </>
      )}
      <DialogAddProducts
        open={dialogAddProductsOpen}
        onClose={handleDialogAddProductsOpen}
        onSave={handleSaveProduct}
      />
      <DialogNewAdvertising
        loading={newAdvertising.isLoading}
        open={dialogNewAdvertisingOpen}
        onClose={handleDialogNewAdvertisingOpen}
        onSave={async (advertising) => {
          await newAdvertising.mutateAsync(advertising);
        }}
      />
    </Stack>
  );
}
